import { createContext, ReactNode, useContext, useMemo, useState } from 'react';
import { ApolloClient, useQuery } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import { GET_OFFICES_QUERY, getOfficesQueryVariables } from '../queries/getOfficesQuery';
import { Office } from '../types/Office';
import { transformOfficeResponse } from '../tools/transformOfficeResponse';
import { useGeographicRegions } from '../../geographic/contexts/GeographicRegionsProvider';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface OfficesContextValue {
    offices: Office[];
    officeById: Record<string, Office>;
    loading: boolean;
    error: any;
    // Filtering
    selectedOfficeGeographicRegionId: string[] | null;
    setSelectedOfficeGeographicRegionId: (id: string[] | null) => void;
    selectedOfficeCountryId: string[] | null;
    setSelectedOfficeCountryId: (id: string[] | null) => void;
    filteredOffices: Office[];
}

const OfficesContext = createContext<OfficesContextValue | undefined>(undefined);

interface OfficesProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const OfficesProvider = ({ children, apolloClient }: OfficesProviderProps) => {
    const { locale } = useLocale();
    const { geographicRegionById } = useGeographicRegions();
    const [selectedOfficeGeographicRegionId, setSelectedOfficeGeographicRegionId] = useState<
        string[] | null
    >(null);
    const [selectedOfficeCountryId, setSelectedOfficeCountryId] = useState<string[] | null>(null);

    const query = GET_OFFICES_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery(query, {
        variables: getOfficesQueryVariables(locale),
        client: apolloClient,
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized: true, queryError: queryError });

    const offices = useMemo(() => {
        const source = queryData ?? fallbackData;
        if (!source) return [];
        return transformOfficeResponse(source);
    }, [queryData, fallbackData]);

    const officeById = useMemo(() => {
        const map: Record<string, Office> = {};
        offices.forEach((o) => {
            map[o.id] = o;
        });
        return map;
    }, [offices]);

    // Filtering logic:
    // If a geographic region is selected, filter offices to those that have at least one country in that region.
    // If a country is selected, filter offices to those that are linked to that country.
    const filteredOffices = useMemo(() => {
        let result = offices;

        if (selectedOfficeGeographicRegionId && selectedOfficeGeographicRegionId.length > 0) {
            const regionCountryIds = selectedOfficeGeographicRegionId.flatMap((regionId) => {
                const region = geographicRegionById[regionId];
                return region ? region.countries.map((c) => c.id) : [];
            });
            result = result.filter((o) => regionCountryIds.includes(o.countryId));
        }

        if (selectedOfficeCountryId && selectedOfficeCountryId.length > 0) {
            result = result.filter((o) => selectedOfficeCountryId.includes(o.countryId));
        }

        return result;
    }, [offices, selectedOfficeGeographicRegionId, selectedOfficeCountryId, geographicRegionById]);

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <OfficesContext.Provider
            value={{
                offices,
                officeById,
                loading,
                error,
                selectedOfficeGeographicRegionId,
                setSelectedOfficeGeographicRegionId,
                selectedOfficeCountryId,
                setSelectedOfficeCountryId,
                filteredOffices,
            }}
        >
            {children}
        </OfficesContext.Provider>
    );
};

function useOffices() {
    const context = useContext(OfficesContext);
    if (!context) {
        throw new Error('useOffices must be used within an OfficesProvider');
    }
    return context;
}

export { OfficesProvider, useOffices };
export type { OfficesContextValue };
